import { useTranslation } from 'react-i18next';
import { Field, Input, MessageBar, MessageBarBody, Radio, RadioGroup, Text, makeStyles, tokens } from '@fluentui/react-components';
import type { TemplateLine } from '../types';
import type { PayrollForm } from '../lib/payrollForm';
import { CalendarDateInput } from './dates/CalendarDateInput';
import { boxesForRun } from '../lib/payrollRoles';

const useStyles = makeStyles({
  box: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  row: { display: 'flex', gap: tokens.spacingHorizontalM, flexWrap: 'wrap', alignItems: 'end' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: tokens.spacingHorizontalM },
  hint: { color: tokens.colorNeutralForeground3 },
});

/**
 * A payroll journal's form (ptc-docs plan 2026-11-04-conversions-wave-5-payroll §2.2): which run, its pay period, and one box
 * per line of the client's payroll template, typed from the report. The journal date (above, on the page) is the pay date.
 * A contractor (1099) run is its own journal: it shows the one box, non-employee comp, posted against clearing.
 */
export default function PayrollRunForm({
  value, onChange, lines, labelOf, disabled, onBlur,
}: {
  value: PayrollForm;
  onChange: (next: PayrollForm) => void;
  /** The client's payroll template lines; empty when it has none. */
  lines: readonly TemplateLine[];
  labelOf: (line: TemplateLine) => string;
  disabled: boolean;
  onBlur?: () => void;
}) {
  const styles = useStyles();
  const { t } = useTranslation();
  const boxes = boxesForRun(lines, value.kind);

  return (
    <div className={styles.box} onBlur={onBlur}>
      <Field label={t('journalPage.payroll.kind')}>
        <RadioGroup
          layout="horizontal"
          value={value.kind}
          disabled={disabled}
          onChange={(_e, data) => onChange({ ...value, kind: data.value === 'contractor' ? 'contractor' : 'regular' })}
        >
          <Radio value="regular" label={t('journalPage.payroll.kinds.regular')} />
          <Radio value="contractor" label={t('journalPage.payroll.kinds.contractor')} />
        </RadioGroup>
      </Field>
      <div className={styles.row}>
        <Field label={t('journalPage.payroll.periodStart')}>
          <CalendarDateInput value={value.periodStart} disabled={disabled} onChange={(v) => onChange({ ...value, periodStart: v })} />
        </Field>
        <Field label={t('journalPage.payroll.periodEnd')}>
          <CalendarDateInput value={value.periodEnd} disabled={disabled} onChange={(v) => onChange({ ...value, periodEnd: v })} />
        </Field>
      </div>
      <Text className={styles.hint}>{t(value.kind === 'contractor' ? 'journalPage.payroll.contractorIntro' : 'journalPage.payroll.intro')}</Text>
      {lines.length === 0 && value.kind === 'regular' ? (
        <MessageBar intent="warning">
          <MessageBarBody>{t('journalPage.payroll.noTemplate')}</MessageBarBody>
        </MessageBar>
      ) : (
        <div className={styles.grid}>
          {boxes.map((line) => (
            <Field key={line.key} label={labelOf(line)} hint={line.note?.trim() || undefined}>
              <Input
                type="number"
                step="0.01"
                disabled={disabled}
                value={value.figures[line.key] ?? ''}
                onChange={(_e, data) => onChange({ ...value, figures: { ...value.figures, [line.key]: data.value } })}
              />
            </Field>
          ))}
        </div>
      )}
    </div>
  );
}
